import { type RefObject, useEffect, useRef } from 'react';

import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'rpso-route-focus';

type FocusMap = Record<string, string>;

const readMap = (): FocusMap => {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
    return typeof parsed === 'object' && parsed !== null ? (parsed as FocusMap) : {}; // as: JSON.parse не знает формы
  } catch {
    return {};
  }
};

const writeMap = (map: FocusMap) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* Без хранилища «назад» ставит фокус на заголовок. */
  }
};

// Успех — только если фокус действительно встал: скрытый или отключённый элемент focus() молча игнорирует
const focusElement = (element: HTMLElement | null): boolean => {
  element?.focus({ preventScroll: true });
  return Boolean(element) && document.activeElement === element;
};

const focusHeading = (main: HTMLElement): boolean => {
  const heading = main.querySelector<HTMLElement>('h1');
  // tabindex ставим здесь, а не на каждой странице: заголовок не входит в обход Tab, но принимает фокус
  if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  return focusElement(heading);
};

const focusLink = (main: HTMLElement, href: string): boolean =>
  focusElement(main.querySelector<HTMLElement>(`a[href="${CSS.escape(href)}"]`));

/**
 * Фокус после SPA-перехода. Ссылка, на которой он был, исчезает вместе со страницей, и фокус
 * проваливался в body — клавиатура и скринридер начинали с начала документа, не узнав о переходе.
 * Новый переход → заголовок h1 новой страницы; «назад» → ссылка, с которой ушли (строка позиции
 * в списке раздела). Прокрутку ведёт ScrollRestoration, фокус её не трогает (preventScroll)
 */
export const useRouteFocus = (mainRef: RefObject<HTMLElement | null>): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  // Переход, для которого фокус уже поставлен. Начальное значение — первая загрузка: там браузер
  // сам ставит фокус в начало документа. Флаг «первый рендер» не годится — StrictMode гоняет эффект дважды
  const handledKeyRef = useRef(location.key);

  // Запоминаем ссылку в main, с которой уходят, — для возврата «назад» на эту страницу.
  // click — потому что Safari по клику мышью ссылку не фокусирует, focusin — для клавиатуры
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const remember = (event: Event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const href = link?.getAttribute('href');
      if (!href) return;
      writeMap({ ...readMap(), [keyRef.current]: href });
    };
    main.addEventListener('focusin', remember);
    main.addEventListener('click', remember);
    return () => {
      main.removeEventListener('focusin', remember);
      main.removeEventListener('click', remember);
    };
  }, [mainRef]);

  useEffect(() => {
    keyRef.current = location.key;
    if (handledKeyRef.current === location.key) return;
    const main = mainRef.current;
    if (!main || location.hash) return;

    const savedHref = navigationType === NavigationType.Pop ? readMap()[location.key] : undefined;
    // Элемент, на который поставили фокус. React может заменить содержимое уже после этого
    // (ленивый чанк, переход внутри одной страницы) — тогда фокус снова проваливается в body
    let focused: Element | null = null;
    const apply = () => {
      if (!((savedHref && focusLink(main, savedHref)) || focusHeading(main))) return false;
      focused = document.activeElement;
      handledKeyRef.current = location.key;
      return true;
    };
    // Где был фокус в момент перехода: ссылка шапки или подвала после перехода остаётся в DOM
    const activeAtStart = document.activeElement;
    apply();

    // Следим до следующего перехода: ждём заголовок, если страница ещё грузится, и повторяем,
    // если заголовок с фокусом заменили. Фокус, который пользователь перевёл сам, не трогаем
    const observer = new MutationObserver(() => {
      const active = document.activeElement;
      const notMovedByUser =
        active === null ||
        active === document.body ||
        (focused === null && active === activeAtStart);
      if (!notMovedByUser) return;
      if (focused === null || !focused.isConnected) apply();
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.key, location.hash, navigationType, mainRef]);
};
